import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function SchoolDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [branchStudentCount, setBranchStudentCount] = useState(null);
  const [branchTeacherCount, setBranchTeacherCount] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [branchName, setBranchName] = useState(null);
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  useEffect(() => {
    // Check if user is authenticated (either school admin or user role)
    const isSchoolAuthenticated = localStorage.getItem('schoolAuthenticated');
    const isUserRoleAuthenticated = localStorage.getItem('userRoleAuthenticated');
    const storedSchoolData = localStorage.getItem('schoolData');
    const userRoleData = localStorage.getItem('userRoleData');
    
    if (isUserRoleAuthenticated && userRoleData) {
      // User logged in via user roles
      try {
        const userData = JSON.parse(userRoleData);
        if (userData.schoolData) {
          setSchoolData(userData.schoolData);
        } else if (userData.school_id) {
          // Fetch school data if not included
          supabase
            .from('schools')
            .select('*')
            .eq('id', userData.school_id)
            .single()
            .then(({ data, error }) => {
              if (!error && data) {
                setSchoolData(data);
              }
            });
        }
        setLoading(false);
        return;
      } catch (e) {
        console.error('Error parsing user role data:', e);
      }
    }
    
    if (!isSchoolAuthenticated || !storedSchoolData) {
      // User is not authenticated, redirect to login
      navigate('/schoollogin');
      return;
    }
    
    // School admin authenticated, load school data
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!schoolData?.id) {
      setBranchName(null);
      return;
    }

    if (!selectedBranchId) {
      setBranchName(null);
      return;
    }

    const fetchBranch = async () => {
      try {
        const { data } = await supabase
          .from('branches')
          .select('branch_name')
          .eq('school_id', schoolData.id)
          .eq('id', selectedBranchId)
          .single();

        setBranchName(data?.branch_name ?? null);
      } catch (error) {
        console.error('Failed to load branch name:', error);
        setBranchName(null);
      }
    };

    fetchBranch();
  }, [schoolData?.id, selectedBranchId]);

  const fetchStats = async (branchIdOverride = null) => {
    if (!schoolData?.id) {
      setBranchStudentCount(null);
      setBranchTeacherCount(null);
      return;
    }

    // Get the current branchId - use override, then hook value, then localStorage
    const currentBranchId = branchIdOverride !== null 
      ? branchIdOverride 
      : (selectedBranchId !== undefined 
          ? selectedBranchId 
          : (() => {
              const saved = localStorage.getItem('selectedBranchId');
              return saved && saved !== 'null' ? parseInt(saved) : null;
            })());

    setStatsLoading(true);
    setStatsError(null);

    try {
      let studentQuery = supabase
        .from('students')
        .select('id', { head: true, count: 'exact' })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      let teacherQuery = supabase
        .from('teachers')
        .select('id', { head: true, count: 'exact' })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (currentBranchId) {
        studentQuery = studentQuery.eq('branch_id', currentBranchId);
        teacherQuery = teacherQuery.eq('branch_id', currentBranchId);
      }

      const [{ count: studentCount, error: studentError }, { count: teacherCount, error: teacherError }] = await Promise.all([
        studentQuery,
        teacherQuery
      ]);

      if (studentError || teacherError) {
        throw studentError || teacherError;
      }

      setBranchStudentCount(studentCount ?? 0);
      setBranchTeacherCount(teacherCount ?? 0);
    } catch (error) {
      console.error('Failed to load branch stats:', error);
      setStatsError('Failed to load branch stats');
      setBranchStudentCount(null);
      setBranchTeacherCount(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Track previous branch ID to detect changes
  const prevBranchIdRef = useRef(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    if (schoolData?.id) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData?.id, selectedBranchId, refreshTrigger]);

  // Auto-refresh page when branch changes on dashboard
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevBranchIdRef.current = selectedBranchId;
      return;
    }

    // Only auto-refresh if:
    // 1. We're on the dashboard page
    // 2. We have school data
    // 3. The branch ID actually changed
    const branchChanged = prevBranchIdRef.current !== selectedBranchId;
    
    if (location.pathname === '/school/dashboard' && schoolData?.id && branchChanged) {
      // Update the ref for next comparison
      prevBranchIdRef.current = selectedBranchId;
      
      // Small delay to allow state updates and data fetching to complete
      const refreshTimeout = setTimeout(() => {
        window.location.reload();
      }, 300);
      
      return () => {
        clearTimeout(refreshTimeout);
      };
    } else {
      // Update the ref even if we don't refresh
      prevBranchIdRef.current = selectedBranchId;
    }
  }, [selectedBranchId, location.pathname, schoolData?.id]);

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Set logout flag to prevent auto-login
      sessionStorage.setItem('schoolLoggedOut', 'true');
      
      // Clear all localStorage
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      localStorage.removeItem('schoolRememberEmail');
      localStorage.removeItem('schoolRememberPassword');
      
      // Redirect to school login page
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear localStorage and redirect even if there's an error
      sessionStorage.setItem('schoolLoggedOut', 'true');
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      localStorage.removeItem('schoolRememberEmail');
      localStorage.removeItem('schoolRememberPassword');
      navigate('/schoollogin');
    }
  };


  const renderContent = () => {
    return (
      <div className="dashboard-content">
        <div className="welcome-section">
          <h1>Welcome to Your School Dashboard</h1>
          <div className="welcome-card">
            <div className="welcome-message">
              <h2>🎉 Welcome, {schoolData?.school_name || 'School'}!</h2>
              <p>You have successfully accessed your School Portal. This is your central command center for managing your school's educational activities.</p>
              {statsError && <div className="stats-error">{statsError}</div>}
              
              <div className="school-stats">
                <div className="stat-item">
                  <span className="stat-number">{branchStudentCount ?? schoolData?.total_students ?? 0}</span>
                  <span className="stat-label">Total Students</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{branchTeacherCount ?? schoolData?.total_teachers ?? 0}</span>
                  <span className="stat-label">Total Teachers</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{schoolData?.school_type?.charAt(0).toUpperCase() + schoolData?.school_type?.slice(1) || 'N/A'}</span>
                  <span className="stat-label">School Type</span>
                </div>
              </div>
              
              <div className="recent-activity">
                <h3>Recent Activity</h3>
                <div className="activity-item">
                  <span className="activity-time">Just now</span>
                  <span className="activity-text">Successfully logged into school portal</span>
                </div>
                <div className="activity-item">
                  <span className="activity-time">Today</span>
                  <span className="activity-text">School dashboard accessed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      {/* Mobile Header */}
      <header className="mobile-dashboard-header">
        <div className="header-left">
          <div className="school-info">
            <div className="school-name-header">
              {schoolData?.school_name || 'School'}
            </div>
            <div className="school-role-header">School</div>
          </div>
        </div>
        <div className="header-right">
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-user-info">
                <div className="user-avatar">🏫</div>
                <div>
                  <div className="mobile-user-name">{schoolData?.school_name || 'School'}</div>
                  <div className="mobile-user-role">School</div>
                </div>
              </div>
              <button className="mobile-logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
            <div className="mobile-menu-tabs">
              <button 
                className={`mobile-menu-tab ${isActive('/school/dashboard') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/dashboard');
                  setMobileMenuOpen(false);
                }}
              >
                Dashboard
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/classes') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/classes');
                  setMobileMenuOpen(false);
                }}
              >
                Classes
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/students') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/students');
                  setMobileMenuOpen(false);
                }}
              >
                Students
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/student-approvals') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/student-approvals');
                  setMobileMenuOpen(false);
                }}
              >
                Student Approvals
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/parents') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/parents');
                  setMobileMenuOpen(false);
                }}
              >
                Parents
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/teachers') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/teachers');
                  setMobileMenuOpen(false);
                }}
              >
                Teachers
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/transport-persons') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/transport-persons');
                  setMobileMenuOpen(false);
                }}
              >
                Drivers
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/cooks') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/cooks');
                  setMobileMenuOpen(false);
                }}
              >
                Cooks
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/receptionists') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/receptionists');
                  setMobileMenuOpen(false);
                }}
              >
                Receptionists
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/schedule') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Holiday Selection
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/timing') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/timing');
                  setMobileMenuOpen(false);
                }}
              >
                School Timings
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/class-schedule') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/class-schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Class Schedule
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/schedule-view') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/schedule-view');
                  setMobileMenuOpen(false);
                }}
              >
                Schedule
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/card-generation') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/card-generation');
                  setMobileMenuOpen(false);
                }}
              >
                Card Generation
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/exam-schedule') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/exam-schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Exams & Tests
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/exam-report-cards') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/exam-report-cards');
                  setMobileMenuOpen(false);
                }}
              >
                Report Cards
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/notices') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/notices');
                  setMobileMenuOpen(false);
                }}
              >
                Notices
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/set-fees') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/set-fees');
                  setMobileMenuOpen(false);
                }}
              >
                Set Fees
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/fee-schedule') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/fee-schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Fee Schedule
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/fee-records') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/fee-records');
                  setMobileMenuOpen(false);
                }}
              >
                Fee Records
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/student-fees') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/student-fees');
                  setMobileMenuOpen(false);
                }}
              >
                Student Fees
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/attendance-records') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/attendance-records');
                  setMobileMenuOpen(false);
                }}
              >
                Attendance Records
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/homework') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/homework');
                  setMobileMenuOpen(false);
                }}
              >
                Homework
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/vehicle') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/vehicle');
                  setMobileMenuOpen(false);
                }}
              >
                Vehicle
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/pickup-locations') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/pickup-locations');
                  setMobileMenuOpen(false);
                }}
              >
                Pickup Location
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/routes') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/routes');
                  setMobileMenuOpen(false);
                }}
              >
                Routes
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/bus-seat-management') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/bus-seat-management');
                  setMobileMenuOpen(false);
                }}
              >
                Bus Seat Management
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/meals') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/meals');
                  setMobileMenuOpen(false);
                }}
              >
                Meals
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/my-account') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/my-account');
                  setMobileMenuOpen(false);
                }}
              >
                My Account
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/settings') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/settings');
                  setMobileMenuOpen(false);
                }}
              >
                Settings
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/curriculum-form') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/curriculum-form');
                  setMobileMenuOpen(false);
                }}
              >
                Curriculum Form
              </button>
              <button 
                className={`mobile-menu-tab ${isActive('/school/annual-calendar-form') ? 'active' : ''}`}
                onClick={() => {
                  navigate('/school/annual-calendar-form');
                  setMobileMenuOpen(false);
                }}
              >
                Annual Calendar Form
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="main-content">
        <div className="content-header desktop-only">
          <h1>School Dashboard</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        
        <div className="content-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default SchoolDashboard;
